<template lang="pug">
.flex.flex-col.gap-6
  h1.unicorn-h1.mt-4 Заявка восстановления

  .flex.flex-col.gap-4(class="md:flex-row md:items-center md:justify-between")
    .flex(class="flex-col md:flex-row md:items-center")
      | Выбор даты
      u-date-picker(
        class="md:ml-4 md:mt-0"
        v-model="date"
        :min-date="firstDate"
        :max-date="maxDate"
        :disabled-dates="disabledDates"
        title-position="left"
        :model-config="modelConfig"
      )
        template(v-slot="{ inputValue, inputEvents }")
          u-button(block v-on="inputEvents") {{ inputValue ? `${inputValue}` : "Выбор даты" }}
    
  .flex.items-center.gap-2(v-if="date && learners.length > 0")
    u-switch.mobile-switch(
      @change="toggleAllCouponsImmediate"
      :modelValue="allCouponsSelected"
      :variant="allCouponsSelected ? 'success' : 'primary'"
      :icon="allCouponsSelected ? 'mdi-checkbox-multiple-marked' : 'mdi-checkbox-multiple-blank-outline'"
      :label="allCouponsSelected ? 'Снять всё' : 'Выбрать всё'" 
      style="order: 2;"
    )

  u-alert(variant="danger" icon="mdi-alert")
    template(#title) Внимание!
    p Эта страница используется ТОЛЬКО для созданния заявок восстановления!
    p Не используйте ее для создания заявок на текущий день!
    p.mt-2.font-medium {{ hint }}

  u-alert(v-if="recoveryAvailable === false" variant="info" icon="mdi-calendar-remove")
    template(#title) Восстановление недоступно
    p Сейчас нет дат, доступных для восстановления заявок. На текущий день оформляется обычная заявка.

  .px-4.my-4(v-if="date && error && !learners.length")
    u-alert(variant="danger" icon="mdi-alert")
      template(#title) Данные класса не загружены
      p {{ loadErrorText }}
      u-button.mt-4(@click="reload" variant="secondary" text) Повторить

  Request.my-8.border-red-400(
    v-if="date && loadedDate === date && (learners.length || !error)"
    ref="requestComponent"
    :key="$route.params.classId"
    :learners="learners"
    :date="date"
    @close-request="closeRequestConfirm"
  )

  u-dialog(persistent width="360" v-model="confirm")
    template
      u-card
        u-card-title Отправка заявки
        u-card-body
          u-alert(variant="danger") Внимание! Это действие нельзя отменить после подтверждения
          p.mt-4.font-medium Вы отправляете заявку в столовую:
          table.mt-2
            tr(v-for="entry in waitingRequest.stats", :key="entry.id")
              td {{ entry.name }}
              td: span.ml-2.text-red-600 {{ entry.count }} шт.
        u-card-actions
          u-spacer
          u-button.mr-2(@click="confirm = false" variant="secondary" text :disabled="submitting") Отмена
          u-button(@click="closeRequest" variant="danger" :disabled="submitting") Продолжить

  u-dialog(width="420" v-model="submitError.visible")
    template
      u-card
        u-card-title Заявка не отправлена
        u-card-body
          p {{ submitError.text }}
          p.mt-2.text-xs.text-gray-500(v-if="submitError.ambiguous")
            | Повторная отправка создаст вторую заявку, если первая всё-таки оформилась.
        u-card-actions
          u-spacer
          u-button.mr-2(@click="checkRequestState" variant="secondary" text :disabled="submitting") Проверить состояние
          u-button(v-if="submitError.retryable" @click="resendRequest" variant="danger" :disabled="submitting") Отправить повторно
</template>

<script>
import { mapState } from "vuex";
import { sortBy } from "lodash";
import Api from "../services/Api";

const DOMAIN_ERROR_TEXTS = {
  402: "Недостаточно средств. Попробуйте обновить страницу (клавиша F5)",
  404: "Заявка не найдена. Попробуйте перезагрузить страницу (клавиша F5)",
  409: "Повторное оформление заявки",
  422: "Ошибка в 'Своем заказе'"
};

const CLOUD_ERROR_TEXTS = {
  500: "Ошибка сервера. Попробуйте позднее",
  502: "Тех. работы на сервере. Попробуйте через 5 минут"
};

const DEFAULT_HINT =
  "Заявки восстановления создаются на прошедшие даты текущего месяца " +
  "(в декабре и мае — на любую дату текущего месяца). На текущий день оформляется обычная заявка.";

export default {
  data() {
    return {
      learners: [],
      loadedDate: "",

      confirm: false,
      waitingRequest: { classId: null, positions: null, date: null, stats: [] },

      submitError: {
        visible: false,
        text: "",
        ambiguous: false,
        retryable: false
      },

      date: "",

      // Окно доступных дат приходит с сервера; при его недоступности считаем локально.
      firstDate: "",
      maxDate: "",
      today: "",
      todayExcluded: true,
      hint: DEFAULT_HINT,
      recoveryAvailable: true,

      submitting: false,
      error: null,

      allCouponsSelected: false,

      modelConfig: {
        type: "string",
        mask: "YYYY-MM-DD"
      }
    };
  },

  computed: {
    ...mapState("connection", ["reloadToken"]),

    /**
     * Сегодня для восстановления запрещено всегда. В обычном месяце оно и так вне
     * [firstDate, maxDate], а в мае/декабре попадает внутрь диапазона — тогда блокируем его.
     */
    disabledDates() {
      if (!this.todayExcluded || !this.today) return [];

      const day = this.parseLocalDate(this.today);
      return [{ start: day, end: day }];
    },

    loadErrorText() {
      if (!this.error) return "Не удалось загрузить данные класса.";

      const { kind, status } = Api.describeError(this.error);

      if (kind === "session") return "Сессия недействительна, войдите заново.";

      return (
        DOMAIN_ERROR_TEXTS[status] ||
        "Нет связи со школьным сервером. Данные класса появятся, как только связь восстановится."
      );
    }
  },

  watch: {
    async date() {
      // Сбрасываем выбор до загрузки: расстановка прежней даты не должна уйти
      // в заявку на новую, пока грузятся её данные.
      this.resetAllToggles();
      await this.loadForDate(this.date);
    },

    async reloadToken() {
      await this.loadForDate(this.date);
    }
  },

  async mounted() {
    await this.loadRules();
  },

  methods: {
    reload() {
      this.$store.dispatch("connection/bumpReload");
    },

    /**
     * Основной путь: окно дат задаёт сервер (правила из БД). Если эндпоинт недоступен —
     * считаем то же окно локально по правилам по умолчанию, страница остаётся рабочей.
     */
    async loadRules() {
      try {
        const { data } = await Api.requestDateRules();
        this.applyWindow(data.recovery, data.today, data.hint);
      } catch (error) {
        await this.applyLocalFallback();
      }
    },

    async applyLocalFallback() {
      let now = new Date();

      try {
        const { data: timestamp } = await Api.time();
        now = new Date(Number(timestamp));
      } catch (error) {
        // Время школы недоступно — берём время устройства.
      }

      const window = this.localWindow(now);
      this.applyWindow(window, this.toIso(now), this.localHint(window, now));
    },

    /**
     * Применяет окно с сервера: `{ available, min, max, todayExcluded }` + `today` школы.
     * Пустое окно — восстановление недоступно, дату выбрать нельзя.
     */
    applyWindow(window, today, hint) {
      this.today = today || "";
      this.hint = hint || DEFAULT_HINT;
      this.todayExcluded = !window || window.todayExcluded !== false;

      if (!window || !window.available || !window.min || !window.max) {
        this.recoveryAvailable = false;
        this.firstDate = "";
        this.maxDate = "";
        this.date = "";
        this.learners = [];
        this.loadedDate = "";
        this.error = null;
        return;
      }

      this.recoveryAvailable = true;
      this.firstDate = window.min;
      this.maxDate = window.max;

      const current = this.date;
      const invalid =
        !current ||
        current < window.min ||
        current > window.max ||
        (this.todayExcluded && current === this.today);

      if (invalid) {
        this.date = this.defaultPick(window);
      }
    },

    /** Дата по умолчанию — «вчера» (последняя доступная), но не позже max. */
    defaultPick(window) {
      const max = this.parseLocalDate(window.max);
      const today = this.today ? this.parseLocalDate(this.today) : new Date();
      const yesterday = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 1
      );
      return this.toIso(max < yesterday ? max : yesterday);
    },

    /** Локальный дубль дефолтных правил: с 1-го числа текущего месяца по вчера; май/декабрь — до конца месяца. */
    localWindow(now) {
      const year = now.getFullYear();
      const month = now.getMonth();
      const min = this.toIso(new Date(year, month, 1));
      const fullMonth = month === 4 || month === 11; // май или декабрь
      const max = fullMonth
        ? this.toIso(new Date(year, month + 1, 0))
        : this.toIso(new Date(year, month, now.getDate() - 1));

      return { available: min <= max, min, max, todayExcluded: true };
    },

    localHint(window, now) {
      if (!window.available) return DEFAULT_HINT;

      const base = `Восстановление доступно с ${window.min} по ${window.max}`;
      const today = this.toIso(now);
      return this.todayExcluded && window.min <= today && today <= window.max
        ? `${base} (кроме ${today}).`
        : `${base}.`;
    },

    parseLocalDate(value) {
      const [year, month, day] = String(value)
        .split("-")
        .map(Number);
      return new Date(year, month - 1, day);
    },

    toIso(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    async loadForDate(date) {
      const { classId } = this.$route.params;

      if (!classId || !date) return;

      try {
        const learners = await this.loadEntrants(classId, date);

        // Дату могли сменить, пока шёл запрос: чужой ответ не подставляем.
        if (this.date !== date) return;

        this.learners = learners;
        this.loadedDate = date;
        this.error = null;
      } catch (error) {
        if (this.date !== date) return;

        this.error = error;

        // Данные для выбранной даты не загрузились. Если на экране остались
        // данные другой даты — их нельзя ни показывать, ни отправлять.
        if (this.loadedDate !== date) {
          this.learners = [];
        }
      }
    },

    async loadEntrants(classId, date) {
      const { data: learners } = await Api.classes.getClassLearners(
        classId,
        date
      );
      return sortBy(learners, ["surname", "name"]);
    },

    toggleAllCouponsImmediate() {
      this.allCouponsSelected = !this.allCouponsSelected;

      requestAnimationFrame(() => {
        if (this.$refs.requestComponent) {
          this.$refs.requestComponent.toggleAllCoupons(this.allCouponsSelected);
        }
      });
    },

    resetAllToggles() {
      this.allCouponsSelected = false;

      if (this.$refs.requestComponent) {
        this.$refs.requestComponent.resetAllSelections();
      }
    },

    async closeRequestConfirm(req) {
      this.confirm = true;
      this.waitingRequest = req;
    },

    async closeRequest() {
      const { classId, body } = this.waitingRequest;

      if (!classId) {
        this.confirm = false;
        return;
      }

      // Повторный вход, пока запрос в полёте, отправил бы вторую заявку.
      if (this.submitting) return;

      this.submitting = true;
      this.submitError = { ...this.submitError, visible: false };

      let sent = false;

      try {
        // recovery=true — сервер проверит дату по окну восстановления.
        await Api.requests.closeRequest(classId, body, this.date, true);
        sent = true;
      } catch (error) {
        this.submitError = this.describeSubmitError(error);
      } finally {
        this.submitting = false;
        this.confirm = false;
      }

      if (!sent) return;

      this.resetAllToggles();
      this.waitingRequest = {
        classId: null,
        positions: null,
        date: null,
        stats: []
      };

      await this.refreshAfterSubmit();
    },

    /**
     * Перечитывание данных после успешной отправки — отдельно от самой отправки,
     * чтобы сбой загрузки не выглядел как «заявка не отправлена».
     * `loadForDate` ошибки не пробрасывает — только фиксирует в `error`.
     */
    async refreshAfterSubmit() {
      await this.loadForDate(this.date);
    },

    /** Автоматического повтора отправки нет: школа создаёт заявку на каждый вызов. */
    describeSubmitError(error) {
      const { kind, reason, status } = Api.describeError(error);

      if (kind === "school_down") {
        const notSent = reason === "no_ping";

        return {
          visible: true,
          ambiguous: !notSent,
          retryable: true,
          text: notSent
            ? "Нет связи со школьным сервером — заявка не отправлена. Повторите отправку, когда связь восстановится."
            : "Школьный сервер не ответил. Заявка могла быть создана — проверьте состояние класса перед повторной отправкой."
        };
      }

      // Устройство не в сети — запрос точно не ушёл.
      if (kind === "offline") {
        return {
          visible: true,
          ambiguous: false,
          retryable: true,
          text:
            "Нет соединения с сетью — заявка не отправлена. Повторите отправку, когда связь восстановится."
        };
      }

      // Таймаут или 5xx: ответа нет, и утверждать «заявка не отправлена» нельзя —
      // она могла создаться. Это важно понимать перед повторной отправкой.
      if (kind === "cloud_down") {
        return {
          visible: true,
          ambiguous: true,
          retryable: true,
          text:
            CLOUD_ERROR_TEXTS[status] ||
            "Сервис не ответил на запрос. Заявка могла быть создана — проверьте состояние класса перед повторной отправкой."
        };
      }

      // Доменный отказ школы: у little в теле ответа есть осмысленное message
      // (например «имеет социальные категории и не может приобретать комплексы»),
      // поэтому показываем его, а статусные тексты оставляем фолбэком.
      const payload = error && error.response && error.response.data;
      const serverMessage =
        payload && typeof payload.message === "string"
          ? payload.message.trim()
          : "";

      return {
        visible: true,
        ambiguous: false,
        retryable: false,
        text:
          serverMessage ||
          DOMAIN_ERROR_TEXTS[status] ||
          "Не удалось отправить заявку. Попробуйте обновить страницу."
      };
    },

    async checkRequestState() {
      this.submitError = { ...this.submitError, visible: false };
      await this.loadForDate(this.date);
    },

    resendRequest() {
      this.submitError = { ...this.submitError, visible: false };
      this.closeRequest();
    }
  }
};
</script>

<style scoped>
.switches-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  min-height: 40px;
}

.mobile-switch,
.desktop-switch {
  isolation: isolate;
  position: relative !important;
  z-index: 0 !important;
  transition: none !important;
}

.mobile-switch >>> *,
.desktop-switch >>> * {
  z-index: 0 !important;
  transition: none !important;
}

.mobile-switch >>> .v-input__icon,
.mobile-switch >>> .mdi,
.mobile-switch >>> i,
.mobile-switch >>> .v-icon,
.mobile-switch >>> svg,
.desktop-switch >>> .v-input__icon,
.desktop-switch >>> .mdi,
.desktop-switch >>> i,
.desktop-switch >>> .v-icon,
.desktop-switch >>> svg {
  z-index: 0 !important;
  position: relative !important;
  transition: none !important;
}

@media (max-width: 767px) {
  .mobile-switch {
    display: inline-flex !important;
    align-items: center !important;
    vertical-align: middle !important;
    flex-shrink: 0;
  }

  .mobile-switch >>> .v-input,
  .mobile-switch >>> .v-input__control,
  .mobile-switch >>> .v-input__slot {
    min-height: 40px !important;
    transition: none !important;
  }
}
</style>
